/**
 * Handles data for a sticker tab.
 */
import axios from '@axios';
import { reactive } from 'vue';
import { useStore } from '@/store';
import { StickerType } from '../../utils/constants';

class StickersTabData {
	// Raw data from the server.
	serverData;

	// Server data in a paginated form.
	data;

	// Vuex store.
	store;

	constructor() {
		this.serverData = reactive({
			configHash: null,
			globalTs: 1,
			panelTheme: null,
			totalBitsUsed: 0,
			bitsCreditAmount: 0,
			partyModeStatus: {
				isEnabled: false,
				isActive: false,
				timeLeft: 0,
			},
			streamerDisplayName: null,
			viewerDisplayName: null,
			panelTitle: null,
			rewards: [],
			stickers: [],
			isOnline: false,
			isMaintenanceMode: false,
			stickersLastModified: [],
			ttsMessageMaxLength: 255,
		});

		this.data = reactive({
			isStreamerOnline: false,
			allStickers: this.createEmptyPagination(undefined),
			powerStickers: this.createEmptyPagination(undefined),
			rewardStickers: this.createEmptyPagination(StickerType.REWARD),
			ttsStickers: this.createEmptyPagination(undefined),
		});

		this.store = useStore();

		this.loadLocalStickerLastModifiedToStore();
	}

	// Load panel data from the store. This is used if the viewer
	// clicks the leaderboard tab and back to the panel. We don't
	// want to reload the stickers tab everytime.
	loadDataFromStore(panelServerResponse, onComplete) {
		const localData = this.getLocalData();
		if (localData) {
			this.setLocalDataInMemory(localData);
		}
		this.setDataAfterServerCall(panelServerResponse, localData);
		onComplete();
	}

	// Make panel data call.
	loadDataFromServer(onComplete, forceFullData) {
		// Build URL to support local caching.
		const localData = this.getLocalData();
		let url;
		let hash;
		let globalTs;
		if (!forceFullData && localData && localData.configHash && localData.globalTs) {
			hash = localData.configHash;
			globalTs = localData.globalTs;
			url = `/data/${localData.configHash}/${localData.globalTs}`;
		} else {
			url = '/data';
		}
		if (localData) {
			this.setLocalDataInMemory(localData);
		}

		axios
			.get(url)
			.then(res => {
				if (res.status === 200) {
					this.store.commit('setExtensionOnline', true);
					// See if cached local data is the latest. If so, we
					// don't reload locally-cached values.
					let isLocalValid = false;
					if (hash && globalTs) {
						if (hash == res.data.configHash && globalTs == res.data.globalTs) {
							isLocalValid = true;
						}
					}

					// Store the response to use to reload the stickers tab, if needed.
					this.store.commit('setPanelServerResponse', res.data);

					if (isLocalValid) {
						//console.log('Local cache is valid.');
						this.setDataAfterServerCall(res.data, localData);
					} else {
						//console.log('Local cache is invalid. Overwriting with server data.');
						this.setDataAfterServerCall(res.data, undefined);
					}
				} else {
					this.store.commit('setExtensionOnline', false);
				}

				//console.log(`All stickers: ${JSON.stringify(data.allStickers, null, 4)}`);
				//console.log(`Reward stickers: ${JSON.stringify(data.rewardStickers, null, 4)}`);
			})
			.catch(() => {
				this.store.commit('setExtensionOnline', false);
			})
			.finally(() => {
				onComplete();
			});
	}

	// Reset local models.
	setLocalDataInMemory(localData) {
		this.serverData.configHash = localData.configHash;
		this.serverData.globalTs = localData.globalTs;
		this.serverData.stickers.length = 0;
		this.serverData.stickers.push(...localData.stickers);

		this.filterAndPaginate(this.data.allStickers, undefined, localData.rewards);
		this.filterAndPaginate(this.data.powerStickers, StickerType.POWER);
		this.filterAndPaginate(this.data.rewardStickers, StickerType.REWARD);
		this.filterAndPaginate(this.data.ttsStickers, StickerType.TTS);
	}

	// Called after we receive data from the server.
	setDataAfterServerCall(responseData, localData) {
		this.serverData.configHash = responseData.configHash;
		this.serverData.globalTs = responseData.globalTs;
		this.serverData.panelTheme = responseData.panelTheme;
		this.serverData.totalBitsUsed = responseData.totalBitsUsed;
		this.serverData.partyModeStatus = responseData.partyModeStatus;
		this.serverData.panelTitle = responseData.panelTitle;
		this.serverData.ttsMessageMaxLength = responseData.ttsMessageMaxLength;

		// Check maintenance mode and streamer online.
		this.store.commit('setExtensionOnline', !responseData.isMaintenanceMode);
		this.store.commit('setStreamerOnline', responseData.isOnline);

		this.store.commit('setBitsCreditAmount', responseData.bitsCreditAmount);

		// Set party mode status.
		this.store.commit('setIsPartyModeOn', this.serverData.partyModeStatus.isActive);

		// Set panel title.
		if (this.serverData.panelTitle) {
			this.store.commit('setPanelTitle', this.serverData.panelTitle);
		}

		// Set TTS sticker approval.
		this.store.commit('setTtsIsManuallyApproveAllStickers', responseData.ttsIsManuallyApproveAllStickers);

		this.serverData.rewards.length = 0;
		this.serverData.rewards.push(...responseData.rewards);

		if (responseData.panelTheme) {
			this.store.commit('setTheme', responseData.panelTheme);
		}

		if (responseData.streamerDisplayName) {
			this.store.commit('setStreamerDisplayName', responseData.streamerDisplayName);
		}

		if (responseData.viewerDisplayName) {
			this.store.commit('setViewerDisplayName', responseData.viewerDisplayName);
		}

		this.store.commit('setCooldownTime', responseData.cooldownTime);
		this.store.commit('setPartyModeCooldownTime', responseData.partyModeCooldownTime);

		this.serverData.stickers.length = 0;
		if (responseData.stickers) {
			this.serverData.stickers.push(...responseData.stickers);
		} else {
			if (localData) {
				this.serverData.stickers.push(...localData.stickers);
			}
		}

		// Save Configuration in LocalStorage
		this.saveLocalData(this.serverData);

		// Hide the Panel Loading Sheet
		setTimeout(() => {
			this.store.commit('setPanelLoading', false);
			this.store.commit('setPanelLoaded', true);
		}, 500);

		// Load stickers into type buckets.
		this.filterAndPaginate(this.data.allStickers, undefined, this.serverData.rewards);
		this.filterAndPaginate(this.data.powerStickers, StickerType.POWER);
		this.filterAndPaginate(this.data.rewardStickers, StickerType.REWARD);
		this.filterAndPaginate(this.data.ttsStickers, StickerType.TTS);

		// Store sticker counts, used to determine which tabs should be shown.
		const stickerCounts = [
			{
				type: StickerType.POWER,
				count: this.data.powerStickers.totalItems,
			},
			{
				type: StickerType.REWARD,
				count: this.data.rewardStickers.totalItems,
			},
			{
				type: StickerType.TTS,
				count: this.data.ttsStickers.totalItems,
			},
		];

		this.store.commit('setStickerCounts', stickerCounts);

		// Store last modified.
		this.serverData.stickersLastModified.length = 0;
		this.serverData.stickersLastModified.push(...responseData.stickersLastModified);
		this.store.commit('setServerStickersLastModified', this.serverData.stickersLastModified);
	}

	getLocalData() {
		try {
			const d = JSON.parse(localStorage.getItem('panel_config'));
			return d ? d : null;
		} catch (err) {
			localStorage.removeItem('panel_config');
			console.error(err);
		}
		return null;
	}

	saveLocalData(localData) {
		try {
			const s = JSON.stringify(localData);
			localStorage.setItem('panel_config', s);
		} catch (err) {
			localStorage.removeItem('panel_config');
			console.error(err);
		}
	}

	removeLocalData() {
		localStorage.removeItem('panel_config');
	}

	// Filter and paginate stickers.
	filterAndPaginate(paginatedItem, type, rewards) {
		let stickers;
		if (type) {
			stickers = this.serverData.stickers.filter(sticker => sticker.stickerType == type);
		} else {
			// For "all stickers", match reward stickers against earned rewards.
			stickers = [];
			let rewardCtr = 0;
			for (const s of this.serverData.stickers) {
				if (s.stickerType != StickerType.REWARD) {
					stickers.push(s);
				} else {
					if (rewards && rewards.length > rewardCtr) {
						if (rewards[rewardCtr].unlocked) {
							stickers.push(s);
						}
						rewardCtr++;
					}
				}
			}
		}

		paginatedItem.type = type;
		paginatedItem.items.length = 0;
		paginatedItem.items.push(...stickers);

		paginatedItem.totalItems = stickers.length;
		paginatedItem.totalPages = Math.ceil(stickers.length / paginatedItem.perPage);
		paginatedItem.currentPage = 0; // Reset to first page on load.

		// Add reward attributes.
		if (type && type == StickerType.REWARD) {
			this.calcRewardAttributes(paginatedItem);
		}
	}

	// Calculate attributes like slot unlocked status to reward stickers.
	calcRewardAttributes(paginatedItem) {
		for (let i = 0; i < paginatedItem.items.length; i++) {
			const item = paginatedItem.items[i];
			const reward = { unlocked: false, pctComplete: 0.0, bitsToUnlock: 0 };

			if (this.serverData.rewards.length > i) {
				const serverReward = this.serverData.rewards[i];

				reward.bitsRequired = serverReward.bitsRequired;
				reward.unlocked = serverReward.unlocked;

				if (reward.unlocked) {
					reward.viewerEarnedInSlot = serverReward.bits;
					reward.bitsToUnlock = 0;
					reward.pctComplete = 1.0;
				} else {
					reward.viewerEarnedInSlot = this.serverData.totalBitsUsed;
					reward.bitsToUnlock = serverReward.bitsRequired - reward.viewerEarnedInSlot;
					reward.pctComplete = this.serverData.totalBitsUsed / serverReward.bitsRequired;
					//console.log(JSON.stringify(reward, null, 4));
				}
			}
			item.reward = reward;
		}
	}

	// Perform processing after bits are used, like updating reward amounts.
	onAfterAfterBitsUsed(bits) {
		this.serverData.totalBitsUsed += bits;
		let doReload = false;
		for (let i = 0; i < this.serverData.rewards.length; i++) {
			const r = this.serverData.rewards[i];
			if (!r.unlocked) {
				const newBits = r.bits + bits;
				if (newBits < r.bitsRequired) {
					r.bits = newBits;
					break;
				} else if (newBits == r.bitsRequired) {
					r.bits = newBits;
					r.unlocked = true;
					doReload = true;
					break;
				} else if (newBits > r.bitsRequired) {
					r.bits = r.bitsRequired;
					r.unlocked = true;
					doReload = true;
					// Update next level.
					if (i + 1 < this.serverData.rewards.length) {
						const nextReward = this.serverData.rewards[i + 1];
						nextReward.bits = this.serverData.totalBitsUsed;
					}
					break;
				}
			}
		}

		// Reload if we hit the next reward level.
		if (doReload) {
			this.loadDataFromServer(() => {}, true);
		}

		this.calcRewardAttributes(this.data.rewardStickers);
	}

	/**
	 * Returns last modified tab clicks from local storage.
	 */
	getLocalStickerLastModified() {
		try {
			const d = JSON.parse(localStorage.getItem('local_sticker_last_mod'));
			return d ? d : null;
		} catch (err) {
			localStorage.removeItem('local_sticker_last_mod');
			console.error(err);
		}
		return null;
	}

	/**
	 * Saves last modified tab clicks from local storage.
	 */
	saveLocalStickerLastModified(arr) {
		localStorage.setItem('local_sticker_last_mod', JSON.stringify(arr));
	}

	/**
	 * Load tab clicks from local storage to the store.
	 */
	loadLocalStickerLastModifiedToStore() {
		const arr = this.getLocalStickerLastModified();
		if (arr) {
			this.store.commit('setLocalStickerLastModified', arr);
		}
	}

	/**
	 * Sets the timestamp when a tab was clicked so we can show / hide the badge when custom
	 * stickers have changed on the server.
	 *
	 * @param {*} type StickerType
	 */
	setTabAsClicked(type) {
		if (!type) {
			return;
		}

		// Set to store.
		const ts = new Date().getTime();
		const storeArr = [];
		for (const storeVal of this.store.getters.activeTabItems) {
			const s = Object.assign({}, storeVal);
			if (s.type == type) {
				s.ts = ts;
			}
			storeArr.push(s);
		}

		this.store.commit('setLocalStickerLastModified', storeArr);

		// Set to local storage.
		let arr = this.getLocalStickerLastModified();
		if (!arr) {
			arr = [];
		}

		let a = arr.find(a => a.type == type);
		if (!a) {
			a = { type: type, ts: ts };
			arr.push(a);
		} else {
			a.ts = ts;
		}

		this.saveLocalStickerLastModified(arr);
	}

	createEmptyPagination(type) {
		if (type && type === StickerType.REWARD) {
			return {
				totalPages: 0,
				totalItems: 0,
				perPage: 3,
				currentPage: 0,
				type: type,
				items: [],
			};
		}
		return {
			totalPages: 0,
			totalItems: 0,
			perPage: 6,
			currentPage: 0,
			type: undefined,
			items: [],
		};
	}
}

export { StickersTabData };
